import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Row,
  Col,
  Button,
  Switch,
  message,
  Skeleton,
  DatePicker,
  notification,
  Typography,
  Select,
  Avatar,
  Checkbox,
  Divider,
} from 'antd'
import {
  MinusCircleFilled,
  CheckCircleFilled,
  PlusCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useParams, useHistory } from 'react-router-dom'
import { useAuthSelector } from 'store/authSlice/authReducer'
import moment from 'moment'
import { compareDateTimeWithNow, disabledTime } from 'utils'
import { MultiImageUploader } from 'components/elements/MultiImageUploader'
import { volunteerOpportunityValidation } from 'validations'
import {
  addVolunteerOpportunity,
  fetchOrganizationVolunteers,
  getVolunteerOpportunity,
  updateVolunteerOpportunity,
} from 'store/volunteerOpportunitySlice/volunteerOpportunityActions'
import { useVolunteerOpportunitySelector } from 'store/volunteerOpportunitySlice/volunteerOpportunityReducer'
import { useDispatch } from 'react-redux'
import { AutoCompleteAddress } from './AutoCompleteAddress'
import AddNewVolunteerModal from '../manage-organization/components/VolunteerOpportunityList/components/CommunityList/AddNewVolunteerModal'

// import ReCAPTCHA from "react-google-recaptcha";

const { TextArea } = Input

const ColorOption = ({ title, value }) => (
  <div style={{ marginLeft: 8 }}>
    <Typography.Text strong>{title}: </Typography.Text>
    {value}
  </div>
)

const colorCodes = [
  {
    value: '#CCE7FF',
    label: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Avatar style={{ backgroundColor: '#CCE7FF' }} />
        <ColorOption title="Light Azure" value="#CCE7FF" />
      </div>
    ),
  },
  {
    value: '#FFD8D8',
    label: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Avatar style={{ backgroundColor: '#FFD8D8' }} />
        <ColorOption title="Light Pink" value="#FFD8D8" />
      </div>
    ),
  },
  {
    value: '#FFEAD1',
    label: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Avatar style={{ backgroundColor: '#FFEAD1' }} />
        <ColorOption title="Creamy Peach" value="#FFEAD1" />
      </div>
    ),
  },
  {
    value: '#C1FFF4',
    label: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Avatar style={{ backgroundColor: '#C1FFF4' }} />
        <ColorOption title="Pale Turquoise" value="#C1FFF4" />
      </div>
    ),
  },
]

const initialQuestion = {
  question: '',
  required: true,
}

const initialValues = {
  title: '',
  scheduleDate: null,
  address: '',
  colorCode: colorCodes[0].value,
  maxRegistration: '',
  description: '',
  organizatonId: '',
  organizationName: '',
  createdBy: '',
  bannerImages: [],
  keywords: [],
  enableVolunteerFeedback: true,
  showOnLandingPage: true,
  opportunityType: 'standalone',
  questions: [initialQuestion],
}

const CreateVolunteerOpportunities = () => {
  const params = useParams()
  const history = useHistory()
  const { user, organization } = useAuthSelector()
  const { volunteers } = useVolunteerOpportunitySelector()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [fetching, setFetched] = useState(false)
  const [opportunityDetail, setOpportunityDetail] = useState(null)

  const [orgAddress, setOrgAddress] = useState(false)
  const [addressValue, setAddressValue] = useState('')

  const [requireVolunteerMailingAddress, setRequireVolunteerMailingAddress] =
    useState(true)
  const [allowFeedback, setAllowFeedback] = useState(true)
  const [showOnLandingPage, setShowOnLandingPage] = useState(true)
  const [inviteVolunteers, setInviteVolunteers] = useState(false)
  const [opportunityType] = useState(initialValues.opportunityType)

  const [isDetailCollapsed, setIsDetailCollapsed] = useState(true)
  const [isQuestionsCollapsed, setIsQuestionsCollapsed] = useState(params?.id)
  const [questions, setQuestions] = useState([{ ...initialQuestion }])
  const [addNewModal, setAddNewModal] = useState(false)

  // For storing default files
  const [defaultFyler, setDefaultFyler] = useState([])

  const dispatch = useDispatch()

  Form.useWatch('description', form)
  Form.useWatch('scheduleDate', form)
  const keywords = Form.useWatch('keywords', form)

  useEffect(() => {
    const getData = async () => {
      setFetched(true)
      if (params.id) {
        const record = await getVolunteerOpportunity(params.id)
        setOpportunityDetail(record)
        if (record) {
          record.scheduleDate = moment(record.scheduleDate)

          const dummyDefaultFlyer = record.bannerImages
            ? record.bannerImages.length
              ? record.bannerImages
              : [record.bannerImages]
            : []

          setDefaultFyler(dummyDefaultFlyer)
          form.setFieldsValue({
            ...record,
          })
          setAddressValue(record?.address)

          if (record?.questions?.length >= 0) {
            setQuestions(record?.questions)
          }
          setAllowFeedback(record?.enableVolunteerFeedback)
          setShowOnLandingPage(record?.showOnLandingPage)
          setRequireVolunteerMailingAddress(
            record?.enableVolunteerMailingAddress,
          )
          setInviteVolunteers(record?.enableVolunteerInvitation || false)
          const vIds = record?.invitedVolunteers?.map((v) => {
            const volunteer = volunteers.find(
              (v2) => v2.phoneNumber === v.volunteerPhoneNumber,
            )
            return volunteer?.iD
          })
          form.setFieldValue('invitedVolunteers', vIds)
        } else {
          message.error('Invalid Opportunity')
          history.push('/settings/create-volunteer-opportunities/')
        }
      }
      setFetched(false)
    }
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, history, form])

  useEffect(() => {
    if (orgAddress) {
      setAddressValue(organization?.Address1)
    }
  }, [orgAddress, organization, form])

  useEffect(() => {
    if (keywords !== undefined) {
      dispatch(fetchOrganizationVolunteers(organization, keywords))
    }
  }, [organization, dispatch, keywords])

  const formulateVolunteers = () => {
    const data = []
    if (form.getFieldValue('invitedVolunteers')) {
      form.getFieldValue('invitedVolunteers').map((v) => {
        const d = volunteers.find((vd) => vd.iD === v)
        data.push({
          volunteerName: `${d?.firstName} ${d?.lastName}`,
          volunteerPhoneNumber: d?.phoneNumber,
        })
        return data
      })
    }
    return data
  }

  const onFinish = async (values) => {
    setLoading(true)
    try {
      if (params.id) {
        const name = `${user?.firstname} ${user?.lastname}`
        const data = {
          ...values,
          userName: name,
          userId: user?.id,
          createdBy: user.email,
          organizationId: organization?.id,
          organizationName: organization?.Name,
          organizationBackgroundImage: organization?.BackgroundImage,
          showOnLandingPage,
          invitedVolunteers: inviteVolunteers ? formulateVolunteers() : [],
          enableVolunteerInvitation:
            inviteVolunteers && formulateVolunteers().length > 0,
          enableQuestioner:
            questions?.length >= 1 && questions[0]?.question !== '',
          enableVolunteerMailingAddress: requireVolunteerMailingAddress,
          questions,
          enableVolunteerFeedback: allowFeedback,
          opportunityType,
        }

        await updateVolunteerOpportunity(
          opportunityDetail.iD,
          data,
          defaultFyler,
        )
        notification.success({
          message: 'Opportunity Updated',
          description: <div>Opportunity updated successfully.</div>,
          icon: (
            <CheckCircleFilled
              style={{
                color: '#00b894',
              }}
            />
          ),
        })
        setLoading(false)
        history.push('/volunteer-opportunities')
      } else {
        const name = `${user?.firstname} ${user?.lastname}`
        const data = {
          ...values,
          userName: name,
          userId: user?.id,
          createdBy: user.email,
          organizationId: organization?.id,
          organizationName: organization?.Name,
          organizationBackgroundImage: organization?.BackgroundImage,
          showOnLandingPage,
          invitedVolunteers: inviteVolunteers ? formulateVolunteers() : [],
          enableVolunteerInvitation:
            inviteVolunteers && formulateVolunteers().length > 0,
          enableQuestioner:
            questions?.length >= 1 && questions[0]?.question !== '',
          enableVolunteerMailingAddress: requireVolunteerMailingAddress,
          questions,
          enableVolunteerFeedback: allowFeedback,
          opportunityType,
        }

        await addVolunteerOpportunity(data, defaultFyler)
        notification.success({
          message: 'Opportunity Created',
          description: <div>Opportunity created successfully.</div>,
          icon: (
            <CheckCircleFilled
              style={{
                color: '#00b894',
              }}
            />
          ),
        })
        setLoading(false)
        form.resetFields()
        history.push('/volunteer-opportunities')
      }
    } catch (error) {
      setLoading(false)
      console.log('Error', error)
      message.error(error?.response?.data?.message || 'Something Went Wrong')
    }
  }

  const buttonText = loading
    ? params.id
      ? 'Updating'
      : 'Creating'
    : params.id
    ? 'Update'
    : 'Create'

  const handleNumberInput = (field, value) => {
    const newValue = value.replaceAll(/,/g, '')
    const val = newValue.replace(/\D/g, '')
    form.setFieldsValue({ [field]: val })
  }

  const handleAddNewQuestion = () => {
    setQuestions((prev) => [...prev, { ...initialQuestion }])
  }

  const handleQuestionDelete = (index) => {
    const questionsUpdated = [...questions]
    questionsUpdated.splice(index, 1)
    setQuestions(questionsUpdated)
  }

  const handleQuestionInput = (e, index) => {
    const val = e.target.value
    const updatedQuestions = [...questions]
    updatedQuestions[index].question = val
    setQuestions(updatedQuestions)
  }

  const handleQuestionRequired = (index) => {
    const updatedQuestions = [...questions]
    updatedQuestions[index].required = !updatedQuestions[index].required
    setQuestions(updatedQuestions)
  }

  const validationRules = volunteerOpportunityValidation

  return (
    <Card
      className="header-solid"
      bordered={false}
      title={[
        <>
          <h6 className="mb-0 t-text-2xl">
            {params.id
              ? 'Update Volunteer Opportunity'
              : 'Add Volunteer Opportunity'}
          </h6>
          <p className="font-regular">Enter Information</p>
        </>,
      ]}
    >
      <Skeleton loading={fetching} active paragraph={{ rows: 18 }}>
        <Form
          form={form}
          layout="vertical"
          key={0}
          onFinish={onFinish}
          name="volunteerOpportunity"
          initialValues={initialValues}
          scrollToFirstError
        >
          <Row gutter={16}>
            <Col xs={24} md={{ span: 12, offset: 6 }}>
              <Card
                title="Opportunity Detail"
                extra={
                  !isDetailCollapsed ? (
                    <PlusCircleOutlined
                      onClick={() => setIsDetailCollapsed((prev) => !prev)}
                      style={{ fontSize: '24px', cursor: 'pointer' }}
                    />
                  ) : (
                    <MinusCircleFilled
                      onClick={() => setIsDetailCollapsed((prev) => !prev)}
                      style={{ fontSize: '24px', cursor: 'pointer' }}
                    />
                  )
                }
              >
                <span style={{ display: isDetailCollapsed ? 'block' : 'none' }}>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        label="Title*"
                        name="title"
                        rules={[validationRules]}
                      >
                        <Input placeholder="Enter title" />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="scheduleDate"
                        label="Schedule*"
                        colon={false}
                        rules={[validationRules]}
                      >
                        <DatePicker
                          format="MM-DD-YYYY hh:mm A"
                          showTime
                          minuteStep={15}
                          className="t-w-full"
                          disabledDate={(d) =>
                            !d || d.isBefore(moment().subtract(1, 'day'))
                          }
                          disabledTime={disabledTime}
                          onChange={(selectedDate) =>
                            !compareDateTimeWithNow(selectedDate, 'before') &&
                            form.setFieldsValue({
                              StartDate: moment(),
                            })
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        label="Max # of Registrations*"
                        name="maxRegistration"
                        rules={[validationRules]}
                      >
                        <Input
                          min={1}
                          inputMode="numeric"
                          style={{ width: '100%' }}
                          placeholder="10"
                          onChange={(e) =>
                            handleNumberInput('maxRegistration', e.target.value)
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        label="Volunteer Duration*"
                        name="volunteerDuration"
                        rules={[validationRules]}
                      >
                        <Input
                          prefix={
                            <p>
                              <b>Hrs.</b>
                            </p>
                          }
                          min={1}
                          inputMode="numeric"
                          style={{ width: '100%' }}
                          placeholder="4"
                          onChange={(e) =>
                            handleNumberInput(
                              'volunteerDuration',
                              e.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={20} md={20}>
                      <Typography.Title level={5}>
                        Requires Volunteer mailing address?
                      </Typography.Title>
                    </Col>
                    <Col xs={4} md={4} className="text-right">
                      <Switch
                        checked={requireVolunteerMailingAddress}
                        onChange={() =>
                          setRequireVolunteerMailingAddress((prev) => !prev)
                        }
                      />
                    </Col>

                    <Col span={24} style={{ marginTop: 10 }}>
                      <AutoCompleteAddress
                        form={form}
                        name="address"
                        label="Place of performance"
                        disabled={orgAddress}
                        value={addressValue}
                        validationRules={validationRules}
                      />
                    </Col>

                    <Col xs={20} md={20}>
                      <Typography.Title level={5}>
                        Same as my org. address?
                      </Typography.Title>
                    </Col>
                    <Col xs={4} md={4} className="text-right">
                      <Switch
                        checked={orgAddress}
                        onChange={() => setOrgAddress((prev) => !prev)}
                      />
                    </Col>

                    <Col span={24} style={{ marginTop: 10 }}>
                      <Form.Item
                        label="Description"
                        name="description"
                        rules={[validationRules]}
                      >
                        <TextArea placeholder="Enter description" rows={4} />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        label="Select Volunteer color code"
                        name="colorCode"
                        rules={[validationRules]}
                      >
                        <Select
                          defaultValue={colorCodes[0].value}
                          style={{ width: '100%' }}
                          onChange={() => null}
                          options={colorCodes}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="bannerImages"
                        label="Banner Image*"
                        colon={false}
                        rules={[validationRules]}
                      >
                        <MultiImageUploader
                          ratio={2 / 1}
                          defaultFiles={
                            form.getFieldValue('bannerImages') &&
                            form.getFieldValue('bannerImages')
                          }
                          onFileChange={(files) =>
                            form.setFieldsValue({
                              bannerImages: files,
                            })
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="keywords"
                        label="Add Keywords"
                        colon={false}
                        rules={[validationRules]}
                      >
                        <Select
                          mode="tags"
                          style={{
                            width: '100%',
                          }}
                          tokenSeparators={[',']}
                          options={[]}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={20} md={20}>
                      <Typography.Title level={5}>
                        Invite Volunteer
                      </Typography.Title>
                    </Col>
                    <Col xs={4} md={4} className="text-right">
                      <Switch
                        checked={inviteVolunteers}
                        onChange={() => setInviteVolunteers((prev) => !prev)}
                      />
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="invitedVolunteers"
                        label={
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span>Search by name / phone</span>
                            <Button
                              size="small"
                              type="primary"
                              className="t-ml-2"
                              onClick={() => setAddNewModal(true)}
                            >
                              Add New
                            </Button>
                          </div>
                        }
                        colon={false}
                        rules={[validationRules]}
                      >
                        <Select
                          disabled={!inviteVolunteers}
                          mode="multiple"
                          optionFilterProp="children"
                          style={{
                            width: '100%',
                          }}
                          key={volunteers?.length}
                          filterOption={(input, option) => {
                            const searchText = input.toLowerCase()
                            const name = option?.label?.toLowerCase()
                            const phoneNumber =
                              option?.phoneNumber?.toLowerCase()
                            return (
                              name.includes(searchText) ||
                              phoneNumber.includes(searchText)
                            )
                          }}
                          options={volunteers?.map((item) => ({
                            value: item.iD,
                            label: `${item.firstName} ${item.lastName}`,
                            phoneNumber: item.phoneNumber,
                          }))}
                        />
                      </Form.Item>
                      <Divider />
                    </Col>

                    <Col xs={20} md={20}>
                      <Typography.Title level={5}>
                        Allow volunteer feedback/rating
                      </Typography.Title>
                    </Col>
                    <Col xs={4} md={4} className="text-right">
                      <Switch
                        checked={allowFeedback}
                        onChange={() => setAllowFeedback((prev) => !prev)}
                      />
                    </Col>

                    <Col md={24} xs={24} style={{ marginTop: 15 }} />
                    <Col xs={20} md={20}>
                      <Typography.Title level={5}>
                        Show on landing page
                      </Typography.Title>
                    </Col>
                    <Col xs={4} md={4} className="text-right">
                      <Switch
                        checked={showOnLandingPage}
                        onChange={() => setShowOnLandingPage((prev) => !prev)}
                      />
                    </Col>
                  </Row>
                </span>
              </Card>

              <Card
                title="Add Questions"
                extra={
                  !isQuestionsCollapsed ? (
                    <PlusCircleOutlined
                      onClick={() => setIsQuestionsCollapsed((prev) => !prev)}
                      style={{ fontSize: '24px', cursor: 'pointer' }}
                    />
                  ) : (
                    <MinusCircleFilled
                      onClick={() => setIsQuestionsCollapsed((prev) => !prev)}
                      style={{ fontSize: '24px', cursor: 'pointer' }}
                    />
                  )
                }
                className="t-mt-6"
              >
                <span
                  style={{
                    display: isQuestionsCollapsed ? 'block' : 'none',
                  }}
                >
                  {questions.map((q, i) => (
                    <React.Fragment key={i}>
                      <Row align="middle" gutter={16}>
                        <Col flex="auto">
                          <Form.Item label={`${i + 1}. Question`}>
                            <Input
                              placeholder="Enter your question"
                              onChange={(e) => handleQuestionInput(e, i)}
                              value={q?.question}
                            />
                          </Form.Item>
                        </Col>
                        <Col>
                          {questions.length > 1 && (
                            <DeleteOutlined
                              style={{ fontSize: 20, cursor: 'pointer' }}
                              onClick={() => handleQuestionDelete(i)}
                            />
                          )}
                        </Col>
                      </Row>
                      <Row justify="end" align="middle">
                        <Checkbox
                          checked={q?.required}
                          onChange={() => handleQuestionRequired(i)}
                        >
                          Required
                        </Checkbox>
                      </Row>
                      <Divider />
                    </React.Fragment>
                  ))}
                  <Col>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddNewQuestion}
                      disabled={questions[questions.length - 1].question === ''}
                    >
                      Add New
                    </Button>
                  </Col>
                </span>
              </Card>

              <Col span={24} className="text-right t-space-x-2 t-mt-6">
                <Button
                  type="ghost"
                  className="px-25"
                  onClick={() => history.goBack()}
                >
                  Cancel
                </Button>
                <Button
                  loading={loading}
                  type="primary"
                  className="px-25"
                  htmlType="submit"
                >
                  {buttonText}
                </Button>
              </Col>
            </Col>
          </Row>
        </Form>
      </Skeleton>
      <AddNewVolunteerModal
        isOpen={addNewModal}
        handler={() => setAddNewModal(false)}
        refreshList={() => {
          dispatch(fetchOrganizationVolunteers(organization, keywords))
        }}
      />
    </Card>
  )
}
export { CreateVolunteerOpportunities }
